import * as THREE from 'three';
import { AppStore } from '../state/store';
import { appendFlyNode, revertLastNode } from '../state/commands';
import { SceneHost } from '../render/scene';
import { flashHint, setDefaultHint } from './toolbar';
import { bankRelativeToRmf, rmfUpFromFrame } from '../core/track/flyBank';
import { rollAt, shortestAngleDelta } from '../core/track/roll';

const NODE_SPACING = 5; // metres between fly-placed nodes
const BASE_SPEED = 22; // m/s
const FAST_SPEED = 55;
const LOOK_SENS = 0.0022;
const TWIST_SENS_RAD = (0.08 * Math.PI) / 180; // radians per wheel deltaY unit
const SPLIT_KEY = 'rcsim-fly-split';

/**
 * Build-mode free-fly in split-screen: left pane is the fly camera, right pane
 * is a free orbit viewer. New track only turns the orbit camera toward the tip.
 * Q reverts a node; Space/Shift raise/lower the orbit view.
 */
export class FlyMode {
  private active = false;
  private locked = false;
  private keys = new Set<string>();
  private lastPlace = new THREE.Vector3();
  private placedSinceEngage = 0;
  private forward = new THREE.Vector3();
  private right = new THREE.Vector3();
  private rmfUp = new THREE.Vector3();
  private deltaQ = new THREE.Quaternion();
  /** Last written / seeded bank — unwraps atan2 so multi-turn twists stay continuous. */
  private lastBankDeg: number | null = null;
  private savedHint = '';
  private closedHintAt = 0;
  private splitUi: HTMLElement;
  private splitDivider: HTMLElement;
  private splitLabelRight: HTMLElement;
  private splitting = false;

  private followBox = new THREE.Box3();
  private followCenter = new THREE.Vector3();
  private followSize = new THREE.Vector3();
  private overviewReady = false;

  constructor(
    private store: AppStore,
    private host: SceneHost,
  ) {
    this.restoreSplitRatio();
    const ui = this.buildSplitUi();
    this.splitUi = ui.root;
    this.splitDivider = ui.divider;
    this.splitLabelRight = ui.labelRight;

    const canvas = host.renderer.domElement;
    canvas.addEventListener('click', (e) => this.onCanvasClick(e));
    canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e), true);
    canvas.addEventListener('pointerup', () => this.onPointerUp());
    document.addEventListener('pointerlockchange', () => this.onLockChange());
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));
    window.addEventListener('mousemove', (e) => this.onMouseMove(e));
    canvas.addEventListener(
      'wheel',
      (e) => {
        if (!this.active) return;
        if (this.locked) {
          e.preventDefault();
          e.stopPropagation();
          // roll about local forward — keeps twisting through loops
          this.deltaQ.setFromAxisAngle(_z, -e.deltaY * TWIST_SENS_RAD);
          this.host.flyCamera.quaternion.multiply(this.deltaQ);
          this.syncCamUp();
          return;
        }
        if (this.isLeftPane(e.clientX)) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      { passive: false },
    );
  }

  get enabled(): boolean {
    return this.active;
  }

  setEnabled(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    this.keys.clear();
    if (on) {
      this.savedHint =
        document.getElementById('mode-hint')?.textContent ??
        'Left: fly · Right: orbit · drag the divider to resize';
      this.host.setSplitScreen(true);
      this.splitUi.classList.remove('hidden');
      this.layoutSplitUi();
      this.engageAtTrackEnd();
      this.syncOrbitToTrack({ reset: true });
      this.host.orbit.enabled = true;
      this.host.orbitWasdEnabled = true;
      setDefaultHint(
        'Left: fly · scroll twists · Q undo node · Right: orbit · Space/Shift up/down · drag divider to resize',
      );
      flashHint('Fly split-screen — Q undoes a node · Space/Shift move the orbit camera up/down');
    } else {
      this.exitLock();
      this.host.setSplitScreen(false);
      this.splitUi.classList.add('hidden');
      this.overviewReady = false;
      this.host.orbit.enabled = this.store.mode === 'build';
      this.host.orbitWasdEnabled = true;
      this.host.camera.up.set(0, 1, 0);
      if (this.savedHint) setDefaultHint(this.savedHint);
    }
  }

  /** Call from the render loop while the fly tool is selected. */
  update(dt: number): void {
    if (!this.active) return;
    if (this.store.mode !== 'build') {
      this.setEnabled(false);
      return;
    }

    if (this.locked) {
      const speed = (this.keys.has('control') ? FAST_SPEED : BASE_SPEED) * dt;
      const cam = this.host.flyCamera;
      cam.getWorldDirection(this.forward);
      this.right.crossVectors(this.forward, cam.up).normalize();
      if (this.right.lengthSq() < 1e-6) this.right.set(1, 0, 0);

      if (this.keys.has('w')) cam.position.addScaledVector(this.forward, speed);
      if (this.keys.has('s')) cam.position.addScaledVector(this.forward, -speed);
      if (this.keys.has('d')) cam.position.addScaledVector(this.right, speed);
      if (this.keys.has('a')) cam.position.addScaledVector(this.right, -speed);

      this.maybePlaceNode();
    }

    this.host.syncFlyMarker();
  }

  /**
   * Entering fly mode: frame an overview once. When track grows/shrinks: only
   * rotate the orbit view to face the tip (camera position stays put).
   */
  private syncOrbitToTrack(opts: { reset?: boolean } = {}): void {
    if (opts.reset || !this.overviewReady) {
      const { center, radius } = this.trackOverview();
      this.host.frameOrbitOverview(center, radius, 1, 40);
      this.overviewReady = true;
      return;
    }
    const tip = this.tipPosition();
    if (tip) this.host.lookOrbitAt(tip);
  }

  private tipPosition(): THREE.Vector3 | null {
    const nodes = this.store.doc.track.nodes;
    if (nodes.length > 0) return nodes[nodes.length - 1].p;
    return this.host.flyCamera.position;
  }

  private trackOverview(): { center: THREE.Vector3; radius: number } {
    this.followBox.makeEmpty();
    const nodes = this.store.doc.track.nodes;
    if (nodes.length === 0) {
      this.followBox.expandByPoint(this.host.flyCamera.position);
    } else {
      for (const n of nodes) this.followBox.expandByPoint(n.p);
      this.followBox.expandByPoint(this.host.flyCamera.position);
    }
    this.followBox.getCenter(this.followCenter);
    this.followBox.getSize(this.followSize);
    const radius = Math.max(this.followSize.length() * 0.5, 25);
    return { center: this.followCenter, radius };
  }

  private buildSplitUi(): { root: HTMLElement; divider: HTMLElement; labelRight: HTMLElement } {
    const wrap = document.getElementById('viewport-wrap')!;
    const el = document.createElement('div');
    el.id = 'fly-split-ui';
    el.className = 'hidden';
    el.innerHTML = `
      <div class="fly-split-label fly-split-left">Fly</div>
      <div class="fly-split-divider" title="Drag to resize"></div>
      <div class="fly-split-label fly-split-right">Orbit</div>`;
    wrap.appendChild(el);
    const divider = el.querySelector('.fly-split-divider') as HTMLElement;
    const labelRight = el.querySelector('.fly-split-right') as HTMLElement;

    divider.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.locked) this.exitLock();
      this.splitting = true;
      divider.setPointerCapture(e.pointerId);
      this.host.orbit.enabled = false;
    });
    divider.addEventListener('pointermove', (e) => {
      if (!this.splitting) return;
      const rect = this.host.renderer.domElement.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / Math.max(rect.width, 1);
      this.host.setSplitRatio(ratio);
      this.layoutSplitUi();
    });
    const endSplit = (e: PointerEvent) => {
      if (!this.splitting) return;
      this.splitting = false;
      try {
        divider.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
      this.host.orbit.enabled = !this.locked;
      this.saveSplitRatio();
    };
    divider.addEventListener('pointerup', endSplit);
    divider.addEventListener('pointercancel', endSplit);

    return { root: el, divider, labelRight };
  }

  private layoutSplitUi(): void {
    const pct = `${this.host.splitRatio * 100}%`;
    this.splitDivider.style.left = pct;
    this.splitLabelRight.style.left = `calc(${pct} + 10px)`;
  }

  private restoreSplitRatio(): void {
    try {
      const raw = localStorage.getItem(SPLIT_KEY);
      if (!raw) return;
      const n = parseFloat(raw);
      if (Number.isFinite(n)) this.host.setSplitRatio(n);
    } catch {
      // ignore
    }
  }

  private saveSplitRatio(): void {
    try {
      localStorage.setItem(SPLIT_KEY, String(this.host.splitRatio));
    } catch {
      // ignore
    }
  }

  private engageAtTrackEnd(): void {
    const nodes = this.store.doc.track.nodes;
    const cam = this.host.flyCamera;
    this.placedSinceEngage = 0;
    this.lastBankDeg = null;
    if (nodes.length === 0) {
      cam.position.set(0, 8, 0);
      cam.up.set(0, 1, 0);
      cam.lookAt(cam.position.x + 1, cam.position.y, cam.position.z);
      this.syncCamUp();
      this.lastPlace.copy(cam.position);
      return;
    }

    const last = nodes[nodes.length - 1];
    const L = this.store.runtime.totalLength;
    const frame = this.store.runtime.frameAt(L);
    cam.position.copy(last.p);
    const tan = last.hOut.lengthSq() > 1e-6 ? last.hOut.clone().normalize() : frame.tan.clone();
    // Match the tip frame (RMF + roll), including inverted loops — not world-up + bank.
    this.setLook(tan, frame.up);
    this.lastBankDeg = rollAt(this.store.doc.rollKeys, L, L, this.store.doc.track.closed);
    this.lastPlace.copy(last.p);
  }

  /** Aim along `forward` with track-frame `up` (for resuming a tip). */
  private setLook(forward: THREE.Vector3, up: THREE.Vector3): void {
    const cam = this.host.flyCamera;
    cam.up.copy(up).normalize();
    cam.lookAt(cam.position.clone().add(forward));
    this.syncCamUp();
  }

  private syncCamUp(): void {
    this.host.flyCamera.up.set(0, 1, 0).applyQuaternion(this.host.flyCamera.quaternion).normalize();
  }

  /**
   * Bank relative to gravity-upright at the tip (matches the chart roll channel).
   */
  private currentBankDeg(): number {
    const cam = this.host.flyCamera;
    cam.getWorldDirection(this.forward);
    this.tipRmfUp(this.rmfUp);
    const raw = bankRelativeToRmf(this.forward, cam.up, this.rmfUp);
    return this.unwrapBank(raw);
  }

  /** Pre-roll gravity-upright at the tip, transported into the plane ⊥ camera forward. */
  private tipRmfUp(out: THREE.Vector3): void {
    const runtime = this.store.runtime;
    if (this.store.doc.track.nodes.length === 0 || runtime.frames.length === 0) {
      out.set(0, 1, 0).addScaledVector(this.forward, -this.forward.y);
      if (out.lengthSq() < 1e-8) {
        out.set(1, 0, 0).addScaledVector(this.forward, -this.forward.x);
      }
      out.normalize();
      return;
    }
    const frame = runtime.frameAt(runtime.totalLength);
    rmfUpFromFrame(frame, out);
    out.addScaledVector(this.forward, -out.dot(this.forward));
    if (out.lengthSq() < 1e-8) {
      out.copy(frame.up).addScaledVector(this.forward, -frame.up.dot(this.forward));
    }
    if (out.lengthSq() < 1e-8) {
      out.set(0, 1, 0).addScaledVector(this.forward, -this.forward.y);
    }
    out.normalize();
  }

  /** Keep multi-turn scroll twists continuous across the ±180° atan2 wrap. */
  private unwrapBank(rawDeg: number): number {
    const prev = this.lastBankDeg;
    if (prev == null) {
      this.lastBankDeg = rawDeg;
      return rawDeg;
    }
    const unwrapped = prev + shortestAngleDelta(prev, rawDeg);
    this.lastBankDeg = unwrapped;
    return unwrapped;
  }

  private maybePlaceNode(): void {
    if (this.store.doc.track.closed) {
      const now = performance.now();
      if (now - this.closedHintAt > 2500) {
        flashHint('Open the track first (uncheck Closed) — fly mode appends at the free end');
        this.closedHintAt = now;
      }
      return;
    }
    const pos = this.host.flyCamera.position;
    if (pos.distanceTo(this.lastPlace) < NODE_SPACING) return;

    this.host.flyCamera.getWorldDirection(this.forward);
    const id = appendFlyNode(this.store, pos.clone(), this.forward, this.currentBankDeg());
    if (!id) return;
    this.lastPlace.copy(pos);
    this.placedSinceEngage++;
    this.store.setSelection([id]);
    this.syncOrbitToTrack();
  }

  private revert(): void {
    if (!this.active) return;
    const nodes = this.store.doc.track.nodes;
    if (nodes.length === 0) {
      flashHint('Nothing to revert');
      return;
    }
    if (!revertLastNode(this.store)) return;
    this.placedSinceEngage = Math.max(0, this.placedSinceEngage - 1);

    const cam = this.host.flyCamera;
    const remaining = this.store.doc.track.nodes;
    if (remaining.length === 0) {
      this.lastPlace.copy(cam.position);
      this.syncOrbitToTrack();
      flashHint('Track cleared');
      return;
    }
    const last = remaining[remaining.length - 1];
    const L = this.store.runtime.totalLength;
    const frame = this.store.runtime.frameAt(L);
    cam.position.copy(last.p);
    const tan = last.hOut.lengthSq() > 1e-6 ? last.hOut.clone().normalize() : frame.tan.clone();
    this.setLook(tan, frame.up);
    this.lastBankDeg = rollAt(this.store.doc.rollKeys, L, L, this.store.doc.track.closed);
    this.lastPlace.copy(last.p);
    this.store.setSelection([last.id]);
    this.syncOrbitToTrack();
    flashHint('Reverted to previous node');
  }

  private isLeftPane(clientX: number): boolean {
    const rect = this.host.renderer.domElement.getBoundingClientRect();
    return clientX - rect.left < rect.width * this.host.splitRatio;
  }

  private onCanvasClick(e: MouseEvent): void {
    if (!this.active || this.locked || this.splitting) return;
    if (this.store.mode !== 'build') return;
    if (!this.isLeftPane(e.clientX)) return;
    this.host.renderer.domElement.requestPointerLock();
  }

  /** Left pane → fly lock; right pane → free mouse for orbit. */
  private onPointerDown(e: PointerEvent): void {
    if (!this.active || this.splitting) return;
    if (this.isLeftPane(e.clientX)) {
      if (!this.locked) this.host.orbit.enabled = false;
      return;
    }
    if (this.locked) this.exitLock();
    this.host.orbit.enabled = true;
    this.host.orbitWasdEnabled = true;
  }

  private onPointerUp(): void {
    if (this.active && !this.locked && !this.splitting) this.host.orbit.enabled = true;
  }

  private onLockChange(): void {
    this.locked = document.pointerLockElement === this.host.renderer.domElement;
    if (this.locked) {
      this.host.orbit.enabled = false;
      this.host.orbitWasdEnabled = false;
      this.seedIfEmpty();
      flashHint('Flying — Q undoes a node · Space/Shift raise/lower the orbit view');
    } else if (this.active) {
      this.host.orbit.enabled = !this.splitting;
      this.host.orbitWasdEnabled = true;
      flashHint('Orbit the right pane · click left to resume flying');
    }
  }

  private seedIfEmpty(): void {
    if (this.store.doc.track.closed) return;
    if (this.store.doc.track.nodes.length > 0) return;
    this.host.flyCamera.getWorldDirection(this.forward);
    const id = appendFlyNode(
      this.store,
      this.host.flyCamera.position.clone(),
      this.forward,
      this.currentBankDeg(),
    );
    if (!id) return;
    this.lastPlace.copy(this.host.flyCamera.position);
    this.placedSinceEngage++;
    this.store.setSelection([id]);
    this.syncOrbitToTrack();
  }

  private exitLock(): void {
    if (document.pointerLockElement === this.host.renderer.domElement) {
      document.exitPointerLock();
    }
    this.locked = false;
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.active || !this.locked) return;
    const cam = this.host.flyCamera;
    // Local-axis deltas: pitch and yaw can pass vertical and complete loops.
    this.deltaQ.setFromAxisAngle(_x, -e.movementY * LOOK_SENS);
    cam.quaternion.multiply(this.deltaQ);
    this.deltaQ.setFromAxisAngle(_y, -e.movementX * LOOK_SENS);
    cam.quaternion.multiply(this.deltaQ);
    this.syncCamUp();
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.active) return;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const k = e.key.toLowerCase();
    if (k === 'w' || k === 'a' || k === 's' || k === 'd') {
      this.keys.add(k);
      e.preventDefault();
    }
    if (e.key === 'Control') this.keys.add('control');
    if (k === 'q' && !e.repeat) {
      e.preventDefault();
      this.revert();
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (k === 'w' || k === 'a' || k === 's' || k === 'd') this.keys.delete(k);
    if (e.key === 'Control') this.keys.delete('control');
  }
}

const _x = new THREE.Vector3(1, 0, 0);
const _y = new THREE.Vector3(0, 1, 0);
const _z = new THREE.Vector3(0, 0, 1);
